import { Shipment, Vehicle, Driver, LoadingSlot, Notification, User } from '../types';

const STORAGE_KEYS = {
  SHIPMENTS: 'yuk_ortagim_shipments',
  VEHICLES: 'yuk_ortagim_vehicles',
  DRIVERS: 'yuk_ortagim_drivers',
  LOADING_SLOTS: 'yuk_ortagim_loading_slots',
  NOTIFICATIONS: 'yuk_ortagim_notifications',
  USER: 'yuk_ortagim_user',
};

// Generic storage functions
function getFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
}

function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
}

// Shipments
export function getShipments(): Shipment[] {
  return getFromStorage<Shipment[]>(STORAGE_KEYS.SHIPMENTS, []);
}

export function saveShipments(shipments: Shipment[]): void {
  saveToStorage(STORAGE_KEYS.SHIPMENTS, shipments);
}

export function addShipment(shipment: Shipment): void {
  const shipments = getShipments();
  shipments.push(shipment);
  saveShipments(shipments);
}

export function updateShipment(id: string, updates: Partial<Shipment>): void {
  const shipments = getShipments();
  const index = shipments.findIndex(s => s.id === id);
  if (index !== -1) {
    shipments[index] = { ...shipments[index], ...updates };
    saveShipments(shipments);
  }
}

export function deleteShipment(id: string): void {
  const shipments = getShipments();
  saveShipments(shipments.filter(s => s.id !== id));
}

// Vehicles
export function getVehicles(): Vehicle[] {
  return getFromStorage<Vehicle[]>(STORAGE_KEYS.VEHICLES, []);
}

export function saveVehicles(vehicles: Vehicle[]): void {
  saveToStorage(STORAGE_KEYS.VEHICLES, vehicles);
}

export function addVehicle(vehicle: Vehicle): void {
  const vehicles = getVehicles();
  vehicles.push(vehicle);
  saveVehicles(vehicles);
}

export function updateVehicle(id: string, updates: Partial<Vehicle>): void {
  const vehicles = getVehicles();
  const index = vehicles.findIndex(v => v.id === id);
  if (index !== -1) {
    vehicles[index] = { ...vehicles[index], ...updates };
    saveVehicles(vehicles);
  }
}

export function deleteVehicle(id: string): void {
  const vehicles = getVehicles();
  saveVehicles(vehicles.filter(v => v.id !== id));
}

// Drivers
export function getDrivers(): Driver[] {
  const drivers = getFromStorage<Driver[]>(STORAGE_KEYS.DRIVERS, []);
  // Takograf bilgilerini ekle (mevcut sürücüler için)
  return drivers.map(driver => ({
    ...driver,
    currentWeekDrivingHours: driver.currentWeekDrivingHours ?? Math.floor(Math.random() * 60), // 0-60 arası rastgele
    twoWeekDrivingHours: driver.twoWeekDrivingHours ?? Math.floor(Math.random() * 95), // 0-95 arası rastgele
  }));
}

export function saveDrivers(drivers: Driver[]): void {
  saveToStorage(STORAGE_KEYS.DRIVERS, drivers);
}

export function addDriver(driver: Driver): void {
  const drivers = getDrivers();
  drivers.push(driver);
  saveDrivers(drivers);
}

export function updateDriver(id: string, updates: Partial<Driver>): void {
  const drivers = getDrivers();
  const index = drivers.findIndex(d => d.id === id);
  if (index !== -1) {
    drivers[index] = { ...drivers[index], ...updates };
    saveDrivers(drivers);
  }
}

export function deleteDriver(id: string): void {
  const drivers = getDrivers();
  saveDrivers(drivers.filter(d => d.id !== id));
}

// Loading Slots
export function getLoadingSlots(): LoadingSlot[] {
  return getFromStorage<LoadingSlot[]>(STORAGE_KEYS.LOADING_SLOTS, []);
}

export function saveLoadingSlots(slots: LoadingSlot[]): void {
  saveToStorage(STORAGE_KEYS.LOADING_SLOTS, slots);
}

export function addLoadingSlot(slot: LoadingSlot): void {
  const slots = getLoadingSlots();
  slots.push(slot);
  saveLoadingSlots(slots);
}

export function updateLoadingSlot(id: string, updates: Partial<LoadingSlot>): void {
  const slots = getLoadingSlots();
  const index = slots.findIndex(s => s.id === id);
  if (index !== -1) {
    slots[index] = { ...slots[index], ...updates };
    saveLoadingSlots(slots);
  }
}

// Notifications
export function getNotifications(): Notification[] {
  return getFromStorage<Notification[]>(STORAGE_KEYS.NOTIFICATIONS, []);
}

export function saveNotifications(notifications: Notification[]): void {
  saveToStorage(STORAGE_KEYS.NOTIFICATIONS, notifications);
}

export function addNotification(notification: Notification): void {
  const notifications = getNotifications();
  notifications.unshift(notification); // Add to beginning
  saveNotifications(notifications);
}

export function markNotificationAsRead(id: string): void {
  const notifications = getNotifications();
  const notification = notifications.find(n => n.id === id);
  if (notification) {
    notification.read = true;
    saveNotifications(notifications);
  }
}

export function markAllNotificationsAsRead(): void {
  const notifications = getNotifications();
  notifications.forEach(n => n.read = true);
  saveNotifications(notifications);
}

export function deleteNotification(id: string): void {
  const notifications = getNotifications();
  saveNotifications(notifications.filter(n => n.id !== id));
}

// User
export function getUser(): User | null {
  return getFromStorage<User | null>(STORAGE_KEYS.USER, null);
}

export function saveUser(user: User): void {
  saveToStorage(STORAGE_KEYS.USER, user);
}

// Initialize with sample data if empty
export function initializeSampleData(): void {
  if (getShipments().length === 0) {
    const sampleShipments: Shipment[] = [
      {
        id: '1',
        orderNumber: 'SEV-2026-001',
        loadingPoint: 'İstanbul Depo',
        deliveryPoint: 'Ankara Mağaza',
        cargoType: 'Doğuş Çay - 500 Koli',
        weight: 12500,
        volume: 25,
        distance: 450,
        loadingDate: '2026-03-14T08:00',
        deliveryDate: '2026-03-14T16:00',
        status: 'in-progress',
        vehicleId: '1',
        driverId: '1',
        transportModel: 'own-fleet',
        cost: 4500,
        estimatedCost: 4200,
        createdAt: '2026-03-13T10:00:00',
        documents: [],
      },
      {
        id: '2',
        orderNumber: 'SEV-2026-002',
        loadingPoint: 'İzmir Depo',
        deliveryPoint: 'Bursa Mağaza',
        cargoType: 'Doğuş Çay - 300 Koli',
        weight: 7500,
        volume: 15,
        distance: 320,
        loadingDate: '2026-03-14T09:00',
        deliveryDate: '2026-03-14T14:00',
        status: 'pending',
        transportModel: 'own-fleet',
        estimatedCost: 3200,
        createdAt: '2026-03-13T11:30:00',
        documents: [],
      },
      {
        id: '3',
        orderNumber: 'SEV-2026-003',
        loadingPoint: 'Ankara Depo',
        deliveryPoint: 'Antalya Mağaza',
        cargoType: 'Doğuş Çay - 800 Koli',
        weight: 20000,
        volume: 40,
        distance: 550,
        loadingDate: '2026-03-13T07:00',
        deliveryDate: '2026-03-13T18:00',
        status: 'delivered',
        vehicleId: '2',
        driverId: '2',
        transportModel: 'own-fleet',
        cost: 7500,
        estimatedCost: 7200,
        createdAt: '2026-03-12T14:00:00',
        documents: [],
      },
      {
        id: '4',
        orderNumber: 'SEV-2026-004',
        loadingPoint: 'İstanbul Depo',
        deliveryPoint: 'İzmir Mağaza',
        cargoType: 'Doğuş Çay - 600 Koli',
        weight: 15000,
        volume: 30,
        distance: 470,
        loadingDate: '2026-03-15T07:00',
        deliveryDate: '2026-03-15T15:00',
        status: 'planned',
        vehicleId: '3',
        driverId: '3',
        transportModel: 'own-fleet',
        estimatedCost: 4700,
        createdAt: '2026-03-13T15:00:00',
        documents: [],
      },
    ];
    saveShipments(sampleShipments);
  }

  if (getVehicles().length === 0) {
    const sampleVehicles: Vehicle[] = [
      {
        id: '1',
        plateNumber: '34 ABC 123',
        type: 'TIR',
        capacity: 24000,
        volumeCapacity: 86,
        status: 'in-use',
        currentLocation: 'Ankara-İstanbul Yolu',
        lastMaintenanceDate: '2026-02-15',
        nextMaintenanceDate: '2026-05-15',
        maintenanceKm: 10000,
        currentKm: 245000,
        fuelType: 'Dizel',
        year: 2022,
        fuelConsumption: 28,
        insuranceExpiryDate: '2026-12-31',
        inspectionExpiryDate: '2026-08-15',
      },
      {
        id: '2',
        plateNumber: '06 XYZ 456',
        type: 'Kamyon',
        capacity: 15000,
        volumeCapacity: 50,
        status: 'available',
        currentLocation: 'Ankara Depo',
        lastMaintenanceDate: '2026-03-01',
        nextMaintenanceDate: '2026-06-01',
        maintenanceKm: 10000,
        currentKm: 89000,
        fuelType: 'Dizel',
        year: 2021,
        fuelConsumption: 25,
        insuranceExpiryDate: '2026-10-20',
        inspectionExpiryDate: '2026-07-10',
      },
      {
        id: '3',
        plateNumber: '35 DEF 789',
        type: 'Kamyonet',
        capacity: 3500,
        volumeCapacity: 15,
        status: 'available',
        currentLocation: 'İzmir Depo',
        lastMaintenanceDate: '2026-02-20',
        nextMaintenanceDate: '2026-05-20',
        maintenanceKm: 5000,
        currentKm: 42000,
        fuelType: 'Dizel',
        year: 2023,
        fuelConsumption: 12,
        insuranceExpiryDate: '2027-01-15',
        inspectionExpiryDate: '2026-09-20',
      },
    ];
    saveVehicles(sampleVehicles);
  }

  if (getDrivers().length === 0) {
    const sampleDrivers: Driver[] = [
      {
        id: '1',
        name: 'Mehmet Yılmaz',
        phone: '0532 123 4567',
        email: 'mehmet.yilmaz@example.com',
        licenseNumber: 'M12345678',
        licenseType: 'E',
        status: 'on-route',
        currentVehicleId: '1',
        totalDeliveries: 245,
        rating: 4.8,
        joinDate: '2020-05-15',
        srcCardNumber: 'SRC123456',
        srcExpiryDate: '2027-05-15',
        workingHours: 6,
        violations: 0,
        currentWeekDrivingHours: 42,
        twoWeekDrivingHours: 78,
      },
      {
        id: '2',
        name: 'Ali Demir',
        phone: '0533 234 5678',
        email: 'ali.demir@example.com',
        licenseNumber: 'A23456789',
        licenseType: 'E',
        status: 'available',
        totalDeliveries: 312,
        rating: 4.9,
        joinDate: '2019-03-10',
        srcCardNumber: 'SRC234567',
        srcExpiryDate: '2026-12-20',
        workingHours: 2,
        violations: 0,
        currentWeekDrivingHours: 28,
        twoWeekDrivingHours: 65,
      },
      {
        id: '3',
        name: 'Ahmet Kaya',
        phone: '0534 345 6789',
        email: 'ahmet.kaya@example.com',
        licenseNumber: 'K34567890',
        licenseType: 'C',
        status: 'available',
        totalDeliveries: 189,
        rating: 4.7,
        joinDate: '2021-07-22',
        srcCardNumber: 'SRC345678',
        srcExpiryDate: '2028-01-15',
        workingHours: 0,
        violations: 1,
        currentWeekDrivingHours: 54,
        twoWeekDrivingHours: 88,
      },
    ];
    saveDrivers(sampleDrivers);
  }

  if (getLoadingSlots().length === 0) {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    
    const sampleSlots: LoadingSlot[] = [
      {
        id: '1',
        locationName: 'İstanbul Depo - Rampa 1',
        date: today,
        startTime: '08:00',
        endTime: '10:00',
        capacity: 3,
        occupied: 2,
        shipmentIds: ['1', '4'],
      },
      {
        id: '2',
        locationName: 'İstanbul Depo - Rampa 1',
        date: today,
        startTime: '10:00',
        endTime: '12:00',
        capacity: 3,
        occupied: 0,
        shipmentIds: [],
      },
      {
        id: '3',
        locationName: 'İstanbul Depo - Rampa 2',
        date: tomorrow,
        startTime: '08:00',
        endTime: '10:00',
        capacity: 3,
        occupied: 0,
        shipmentIds: [],
      },
    ];
    saveLoadingSlots(sampleSlots);
  }

  if (getNotifications().length === 0) {
    const sampleNotifications: Notification[] = [
      {
        id: '1',
        type: 'warning',
        title: 'Bakım Zamanı Yaklaşıyor',
        message: '34 ABC 123 plakalı araç 5000 km sonra bakıma girecek',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        read: false,
      },
      {
        id: '2',
        type: 'info',
        title: 'Yeni Sevkiyat Oluşturuldu',
        message: 'SEV-2026-004 numaralı iş emri başarıyla oluşturuldu',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        read: false,
      },
      {
        id: '3',
        type: 'success',
        title: 'Teslimat Tamamlandı',
        message: 'SEV-2026-003 numaralı sevkiyat başarıyla teslim edildi',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        read: true,
      },
      {
        id: '4',
        type: 'warning',
        title: 'SRC Kartı Yenilenecek',
        message: 'Ali Demir\'in SRC kartı 3 ay içinde sona erecek',
        timestamp: new Date(Date.now() - 172800000).toISOString(),
        read: false,
      },
    ];
    saveNotifications(sampleNotifications);
  }

  if (!getUser()) {
    const sampleUser: User = {
      id: '1',
      name: 'Mahir Bey',
      email: 'mahir@doguscay.com',
      role: 'admin',
      company: 'Doğuş Çay A.Ş.',
      phone: '0532 999 8877',
      preferences: {
        notifications: true,
        emailAlerts: true,
        language: 'tr',
      },
    };
    saveUser(sampleUser);
  }
}